import type {
  ComponentPropsWithoutRef,
  ElementType,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
} from "react";
import styles from "./Surface.module.css";

/**
 * Surface — card / panel primitive.
 *
 * A bounded visual region with background, border, and radius. Used as
 * the outer shell for cards and as the container for bounded sections
 * (PairingView code display, etc.).
 *
 * `tone` selects one of the three Figma background tokens:
 *   - `primary`   → `--bg-primary`   (card default, highest contrast)
 *   - `secondary` → `--bg-secondary` (subtle panel / hover target)
 *   - `tertiary`  → `--bg-tertiary`  (deeper panel, rarely used)
 *
 * Variants are deliberately capped at 3 + an `interactive` flag. If a
 * consumer needs a fourth tone, consider whether it's a token shortage
 * (add a semantic token in Figma) or a one-off (use raw CSS in the
 * consuming component's .module.css).
 *
 * NOTE on `as` polymorphism: `SurfaceProps` extends `<div>` attributes
 * only, same simplification as Box/Text. Use the native element or cast
 * if you need element-specific attrs (e.g. `as="article"` still type-
 * checks as a div's attribute set).
 *
 * Accessibility (`interactive`): when `interactive` is set on a non-button
 * element (the default `div`, or any `as=` override that isn't natively
 * focusable), Surface automatically adds `tabIndex={0}`, `role="button"`,
 * and a keyboard handler that maps Enter/Space to `onClick` — so the
 * `:focus-visible` outline and the click affordance work via keyboard,
 * not just mouse.
 *
 * Consumer `role` and `tabIndex` props win over the defaults (spread order
 * is consumer-last). Consumer `onKeyDown` is **merged** — the Enter/Space
 * shim runs first and the consumer handler is always called afterward, so
 * adding a custom `onKeyDown` (e.g. to close on Escape) does NOT forfeit
 * keyboard activation.
 *
 * Interactive surfaces without an `onClick` should be rare; prefer Button
 * for true buttons — Surface is for card-like regions that happen to be
 * clickable (e.g. a notification card whose whole body navigates).
 */

type Tone = "primary" | "secondary" | "tertiary";
type Padding = "sm" | "md" | "lg";

// Elements that natively trigger a click on keyboard activation (Enter for
// anchors/selects/etc; Enter+Space for buttons). When one of these is used
// as `as`, the onKeyDown shim below must NOT also dispatch a synthetic click.
const NATIVELY_ACTIVATABLE = new Set(["button", "a", "input", "select", "textarea"]);

export type SurfaceProps = {
  /** Element to render. Defaults to `div`. */
  as?: ElementType;
  /** Background tone. Defaults to `primary`. */
  tone?: Tone;
  /** Internal padding. Defaults to `md`. */
  padding?: Padding;
  /** Adds a hover state (background steps up one tone, cursor: pointer). */
  interactive?: boolean;
  className?: string;
  children?: ReactNode;
} & Omit<ComponentPropsWithoutRef<"div">, "className" | "children">;

export function Surface({
  as,
  tone = "primary",
  padding = "md",
  interactive,
  className,
  children,
  ...rest
}: SurfaceProps) {
  const Component = (as ?? "div") as ElementType;
  const classes = [
    styles.surface,
    styles[`tone-${tone}`],
    styles[`padding-${padding}`],
    interactive ? styles.interactive : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Keyboard shim for interactive non-button surfaces. A plain <div> is not
  // focusable and does not fire click on Enter/Space, so the CSS
  // :focus-visible rule + cursor:pointer affordance would be mouse-only
  // without this. We add tabIndex + role + onKeyDown when `interactive` is
  // set.
  //
  // The onKeyDown SKIPS the synthetic click when `as` resolves to a
  // natively-activatable element (button/a/input/select/textarea) — those
  // elements already dispatch a click on Enter (and button/input also on
  // Space). Dispatching our own click on top would double-fire the
  // consumer's onClick. The JSDoc steers consumers toward `Button` for true
  // buttons, so this branch is rare, but the guard keeps the invariant
  // explicit.
  //
  // Consumer onKeyDown is MERGED (not replaced) so that passing a custom
  // handler (e.g. to close on Escape) doesn't silently forfeit the
  // Enter/Space activation shim. The shim logic fires first; the consumer
  // handler is called afterward regardless of which key was pressed.
  const tagName = typeof Component === "string" ? Component : "";
  const isNativelyActivatable = NATIVELY_ACTIVATABLE.has(tagName);

  // Destructure the consumer's onKeyDown before spreading so we can merge
  // it into the shim rather than letting ...rest clobber interactiveA11y.
  const { onKeyDown: consumerKeyDown, ...restWithoutKeyDown } = rest;

  const interactiveA11y = interactive
    ? {
        role: "button",
        tabIndex: 0,
        onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => {
          if (!isNativelyActivatable && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            (event.currentTarget as HTMLElement).click();
          }
          // Cast to HTMLDivElement — SurfaceProps extends div attrs, so the
          // consumer's handler types its event against HTMLDivElement. At
          // runtime the actual element may be a different tag (e.g. "article")
          // but all the properties the handler can access are present.
          consumerKeyDown?.(event as ReactKeyboardEvent<HTMLDivElement>);
        },
      }
    : null;

  return (
    <Component className={classes} {...interactiveA11y} {...restWithoutKeyDown}>
      {children}
    </Component>
  );
}
